import type { ComponentPropsWithoutRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { remarkGithubAlerts, type AlertType } from "../../lib/markdown/remarkGithubAlerts";
import { cn } from "../../utils/cn";
import { Icon } from "./Icon";

type MarkdownContentProps = {
  content: string;
  className?: string;
  /** Compact mode: smaller headings, tighter spacing */
  compact?: boolean;
};

// ── GitHub Alert Callout ────────────────────────────────────────────────────

const ALERT_CONFIG: Record<AlertType, { icon: string; label: string; border: string; bg: string; text: string }> = {
  note: {
    icon: "lucide:info",
    label: "Note",
    border: "border-blue-500/40",
    bg: "bg-blue-500/5",
    text: "text-blue-400",
  },
  tip: {
    icon: "lucide:lightbulb",
    label: "Tip",
    border: "border-emerald-500/40",
    bg: "bg-emerald-500/5",
    text: "text-emerald-400",
  },
  important: {
    icon: "lucide:alert-circle",
    label: "Important",
    border: "border-violet-500/40",
    bg: "bg-violet-500/5",
    text: "text-violet-400",
  },
  warning: {
    icon: "lucide:alert-triangle",
    label: "Warning",
    border: "border-amber-500/40",
    bg: "bg-amber-500/5",
    text: "text-amber-400",
  },
  caution: {
    icon: "lucide:shield-alert",
    label: "Caution",
    border: "border-red-500/40",
    bg: "bg-red-500/5",
    text: "text-red-400",
  },
};

const GfmCallout = ({
  alertType,
  children,
}: {
  alertType: AlertType;
  children: React.ReactNode;
}) => {
  const config = ALERT_CONFIG[alertType];
  return (
    <div
      className={cn(
        "my-2 rounded-md border-l-4 px-3 py-2",
        config.border,
        config.bg,
      )}
      role="note"
    >
      <div className={cn("mb-1 flex items-center gap-1.5 text-xs font-semibold", config.text)}>
        <Icon icon={config.icon} className="h-3.5 w-3.5" />
        {config.label}
      </div>
      <div className="text-sm text-muted-foreground [&>p]:mb-0">{children}</div>
    </div>
  );
};

// ── MarkdownContent ─────────────────────────────────────────────────────────

export const MarkdownContent = ({ content, className, compact = false }: MarkdownContentProps) => (
  <div
    className={cn(
      compact ? "space-y-1.5 text-xs text-muted-foreground" : "space-y-2 text-sm text-muted-foreground",
      className,
    )}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkGithubAlerts]}
      components={{
      h1: ({ className: cls, ...props }) => (
        <h1
          className={cn(compact ? "text-lg font-semibold text-foreground" : "text-2xl font-semibold text-foreground", cls)}
          {...props}
        />
      ),
      h2: ({ className: cls, ...props }) => (
        <h2
          className={cn(compact ? "text-base font-semibold text-foreground" : "text-xl font-semibold text-foreground", cls)}
          {...props}
        />
      ),
      h3: ({ className: cls, ...props }) => (
        <h3
          className={cn(compact ? "text-sm font-semibold text-foreground" : "text-lg font-semibold text-foreground", cls)}
          {...props}
        />
      ),
      p: ({ className: cls, ...props }) => (
        <p className={cn("text-sm text-muted-foreground", cls)} {...props} />
      ),
      a: ({ className: cls, ...props }) => (
        <a
          className={cn("text-primary-text underline underline-offset-2", cls)}
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        />
      ),
      ul: ({ className: cls, ...props }) => (
        <ul className={cn("list-disc space-y-1 pl-5", cls)} {...props} />
      ),
      ol: ({ className: cls, ...props }) => (
        <ol className={cn("list-decimal space-y-1 pl-5", cls)} {...props} />
      ),
      code: ({ className: cls, children, ...props }: ComponentPropsWithoutRef<"code"> & { inline?: boolean }) => {
        // Fenced code blocks get a className like "language-js"
        const isBlock = typeof cls === "string" && cls.startsWith("language-");
        if (isBlock) {
          return (
            <code
              className={cn(
                "block overflow-x-auto qb-scrollbar rounded-md border border-border/50 bg-muted/50 p-3 font-mono text-xs leading-relaxed text-foreground",
                cls,
              )}
              {...props}
            >
              {children}
            </code>
          );
        }
        // Inline code
        return (
          <code
            className={cn(
              "rounded-sm border border-border/40 bg-muted/60 px-1 py-0.5 font-mono text-[0.85em] text-foreground",
              cls,
            )}
            {...props}
          >
            {children}
          </code>
        );
      },
      pre: ({ children, ...props }) => (
        <pre className="my-2 overflow-hidden rounded-md" {...props}>
          {children}
        </pre>
      ),
      blockquote: ({
        children,
        ...props
      }: ComponentPropsWithoutRef<"blockquote"> & { "data-alert-type"?: string }) => {
        const alertType = props["data-alert-type"] as AlertType | undefined;
        if (alertType && alertType in ALERT_CONFIG) {
          return <GfmCallout alertType={alertType}>{children}</GfmCallout>;
        }
        return (
          <blockquote
            className="my-2 border-l-4 border-border/60 pl-3 text-sm italic text-muted-foreground"
            {...props}
          >
            {children}
          </blockquote>
        );
      },
      table: ({ className: cls, ...props }) => (
        <div className="my-2 overflow-x-auto qb-scrollbar rounded-md border border-border/50">
          <table className={cn("w-full text-sm", cls)} {...props} />
        </div>
      ),
      thead: (props) => <thead className="border-b border-border/50 bg-muted/30" {...props} />,
      th: ({ className: cls, ...props }) => (
        <th className={cn("px-3 py-1.5 text-left text-xs font-medium text-muted-foreground", cls)} {...props} />
      ),
      td: ({ className: cls, ...props }) => (
        <td className={cn("px-3 py-1.5 text-sm", cls)} {...props} />
      ),
      hr: () => <hr className="my-3 border-border/40" />,
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
);
